import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  HeroControlItem,
  HeroControlNote,
  HeroControlPanel,
  HeroControlSection,
  buttonVariants,
} from '@wundo/ui';
import { FolderPlus } from 'lucide-react';
import type { MeetingPageDocument, MeetingPageRecap } from '@wundo/shared/meetingPage.ts';
import { MeetingDocumentList } from './MeetingDocumentList.tsx';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { useMeetingSourcesPanelAsked } from '@/lib/meetingSourcesPanel.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingSourcesControlProps {
  /** How much Wundo carries into this conversation: the earlier ones in it. */
  earlier: number;
  /** The last of them, when its verslag is written — the only one nameable here. */
  previous: MeetingPageRecap | null;
  /** What this conversation is called, which is what the earlier ones are called too. */
  name: string;
  /** The files put on this conversation, oldest first. */
  documents: MeetingPageDocument[];
  /** Whether a file is on its way up right now. */
  uploading: boolean;
  /** Whether the last one that went up came back refused. */
  uploadFailed: boolean;
  onAddDocument: (file: File) => void;
  onDeleteDocument: (document: MeetingPageDocument) => void;
}

/**
 * What Wundo reads along at this conversation.
 *
 * A preparation that quotes a conversation from six weeks ago is unnerving
 * until you can see where it came from. So the count stands in the row under
 * the hero, and behind it is the list itself — with the way to open the
 * previous conversation, because "where did that come from?" is answered by
 * reading it, not by being told it exists.
 *
 * It is also where a document is handed over, which is why the control stands
 * on a first conversation too: with nothing behind it yet, the panel is still
 * the one place to say "read this along".
 */
export function MeetingSourcesControl({
  earlier,
  previous,
  name,
  documents,
  uploading,
  uploadFailed,
  onAddDocument,
  onDeleteDocument,
}: MeetingSourcesControlProps) {
  /*
   * The series counts once, however many sittings it has had. What Wundo
   * carries in is one running memory of the conversation, not fifty separate
   * verslagen — and a chip that read "50 bronnen" over a panel with one row
   * would be wrong twice. The depth of the series lives on the row instead.
   */
  const count = (earlier > 0 ? 1 : 0) + documents.length;

  /*
   * Opened by its own pill, and by the chat drawer beside it: the nudge about
   * documents has nowhere to send anybody, because the place to hand one over
   * is right here.
   *
   * Asking is counted rather than flagged, and the panel remembers the count it
   * was last closed at — so it stands open while a request is newer than that
   * refusal, closes the ordinary ways, and opens again the next time it is
   * asked for. A fresh page starts from whatever the count is by then, so
   * coming back to a meeting never reopens a panel somebody already closed.
   */
  const asked = useMeetingSourcesPanelAsked();
  const [closedAt, setClosedAt] = useState(asked);
  const [openedHere, setOpenedHere] = useState(false);
  const open = openedHere || asked > closedAt;

  return (
    <HeroControlPanel
      icon={<FolderPlus aria-hidden='true' />}
      open={open}
      onOpenChange={(next) => {
        setOpenedHere(next);
        if (!next) setClosedAt(asked);
      }}
      /*
       * Two keys rather than one with a number in it: "1 bronnen" is wrong in
       * every language this ships in, and a series with exactly one earlier
       * conversation behind it is the ordinary case, not an edge one.
       */
      value={
        count === 1 ? m.meeting_sources_count_one() : m.meeting_sources_count({ count })
      }
      title={m.meeting_sources_title()}
      description={m.meeting_sources_body()}
    >
      {/*
        Only where there is a conversation before this one. A section headed
        "Vorige gesprekken" with nothing under it is a door to an empty room —
        while the documents section is always there, because it is the way in.
      */}
      {earlier > 0 && (
        <HeroControlSection title={m.meeting_sources_previous()}>
          {previous && (
            <HeroControlItem
              name={name}
              meta={
                earlier === 1
                  ? `${formatDayAndMonth(new Date(previous.meeting_starts_at))} · ${m.meeting_sources_previous_meta()}`
                  : `${formatDayAndMonth(new Date(previous.meeting_starts_at))} · ${m.meeting_sources_previous_meta_many({ count: earlier })}`
              }
              action={
                <Link
                  to='/meetings/$meetingId'
                  params={{ meetingId: previous.meeting_id }}
                  className={buttonVariants({ variant: 'link', size: 'sm' })}
                >
                  {m.meeting_sources_open()}
                </Link>
              }
            />
          )}
          <HeroControlNote>{m.meeting_sources_note()}</HeroControlNote>
        </HeroControlSection>
      )}

      <HeroControlSection title={m.meeting_sources_documents()}>
        <MeetingDocumentList
          documents={documents}
          uploading={uploading}
          uploadFailed={uploadFailed}
          onAdd={onAddDocument}
          onDelete={onDeleteDocument}
        />
      </HeroControlSection>
    </HeroControlPanel>
  );
}
